import { useEffect, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Trash2, Plus } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api/quickpick/avoid-list`;

const KindList = ({ kind, label, items, onAdd, onRemove }) => {
  const [id, setId] = useState('');
  const [note, setNote] = useState('');
  const submit = async () => {
    if (!id.trim()) return;
    await onAdd(kind, id.trim(), note);
    setId(''); setNote('');
  };
  return (
    <div className="flex-1 min-w-[320px]" data-testid={`avoid-${kind}-section`}>
      <h3 className="text-sm font-semibold text-slate-200 mb-2">Avoid {label}s <span className="text-slate-500 font-normal">({items.length})</span></h3>
      <div className="flex gap-2 mb-2">
        <input value={id} onChange={(e) => setId(e.target.value)} onKeyDown={(e) => e.key === 'Enter' && submit()}
          placeholder={`${label} ID (e.g. ${kind === 'trainer' ? '000000001304TE' : '000000108912JE'})`}
          className="flex-1 px-2 py-1.5 bg-slate-800 border border-slate-700 text-slate-200 text-xs rounded-lg font-mono focus:outline-none focus:border-emerald-500"
          data-testid={`avoid-${kind}-id-input`} />
        <input value={note} onChange={(e) => setNote(e.target.value)} onKeyDown={(e) => e.key === 'Enter' && submit()}
          placeholder="Note (optional)"
          className="w-36 px-2 py-1.5 bg-slate-800 border border-slate-700 text-slate-200 text-xs rounded-lg focus:outline-none focus:border-emerald-500"
          data-testid={`avoid-${kind}-note-input`} />
        <button onClick={submit} className="px-2 py-1.5 rounded-lg bg-emerald-600 hover:bg-emerald-500 text-white text-xs flex items-center gap-1 transition-colors"
          data-testid={`avoid-${kind}-add-btn`}><Plus className="w-3 h-3" />Add</button>
      </div>
      <table className="w-full text-xs">
        <thead className="text-slate-500"><tr><th className="text-left py-1">ID</th><th className="text-left py-1">Name</th><th className="text-left py-1">Note</th><th /></tr></thead>
        <tbody>
          {items.length === 0 && <tr><td colSpan={4} className="py-2 text-slate-600">None listed.</td></tr>}
          {items.map((it) => (
            <tr key={it.id} className="border-t border-slate-800" data-testid={`avoid-${kind}-row-${it.id}`}>
              <td className="py-1.5 font-mono text-slate-300">{it.id}</td>
              <td className={`py-1.5 ${it.name ? 'text-slate-200' : 'text-amber-400 italic'}`} data-testid={`avoid-${kind}-name-${it.id}`}>{it.name || 'name not found yet'}</td>
              <td className="py-1.5 text-slate-400">{it.note}</td>
              <td className="py-1.5 text-right">
                <button onClick={() => onRemove(kind, it.id)} className="text-slate-500 hover:text-rose-400 transition-colors" title="Remove"
                  data-testid={`avoid-${kind}-remove-${it.id}`}><Trash2 className="w-3.5 h-3.5" /></button>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
};

export const AvoidListCard = () => {
  const [items, setItems] = useState([]);
  useEffect(() => {
    axios.get(API, { withCredentials: true }).then((r) => setItems(r.data.items || [])).catch(() => toast.error('Could not load avoid lists'));
  }, []);
  const onAdd = async (kind, id, note) => {
    try { const r = await axios.post(API, { kind, id, note }, { withCredentials: true }); setItems(r.data.items || []); toast.success(`${id} added`); }
    catch (e) { toast.error(e?.response?.data?.detail || 'Add failed'); }
  };
  const onRemove = async (kind, id) => {
    if (!window.confirm(`Remove ${id} from the ${kind} avoid list?`)) return;
    try { const r = await axios.delete(`${API}/${kind}/${encodeURIComponent(id)}`, { withCredentials: true }); setItems(r.data.items || []); toast.success(`${id} removed`); }
    catch { toast.error('Remove failed'); }
  };
  return (
    <div className="bg-slate-900 border border-slate-800 rounded-xl p-4 mb-4" data-testid="avoid-list-card">
      <h2 className="text-lg font-semibold text-white">Trainer / Jockey Avoid Lists</h2>
      <p className="text-slate-400 text-sm mb-3">
        IDs from the handicapping CSV (<code>trainer_id</code> / <code>jockey_id</code>). Auto Tri Bet <b>holds</b> (records, emails, doesn't send) a play
        when a keyed horse has a listed trainer or jockey; manual ADW sends warn first. Blank IDs are treated as clear.
      </p>
      <div className="flex gap-6 flex-wrap">
        <KindList kind="trainer" label="Trainer" items={items.filter((i) => i.kind === 'trainer')} onAdd={onAdd} onRemove={onRemove} />
        <KindList kind="jockey" label="Jockey" items={items.filter((i) => i.kind === 'jockey')} onAdd={onAdd} onRemove={onRemove} />
      </div>
    </div>
  );
};
